// Hợp đồng sự kiện của hr-service (bên phát đặt tên). Bên tiêu thụ: identity, crm, booking, deal,
// attendance, performance, commission, notification, audit.
import { z } from 'zod';

export type ContractType = 'official' | 'probation' | 'ctv';

export const EVENTS = {
  employeeRegistered: 'hr.employee.registered',
  employeeProfileUpdated: 'hr.employee.profileUpdated',
  employeeContractTypeChanged: 'hr.employee.contractTypeChanged',
  employeeTeamChanged: 'hr.employee.teamChanged',
  employeeDefaultTargetChanged: 'hr.employee.defaultTargetChanged',
  employeeOffboarded: 'hr.employee.offboarded',
  employeeReactivated: 'hr.employee.reactivated',
  teamCreated: 'hr.team.created',
  teamUpdated: 'hr.team.updated',
  teamDeleted: 'hr.team.deleted',
  teamRosterChanged: 'hr.team.rosterChanged',
  salaryChanged: 'hr.salary.changed',
  /** Bản ghi truy cập nhạy cảm: mỗi lần đọc GetSalaryRecord / GetSalarySnapshot được cấp quyền (AT34). */
  salaryViewed: 'hr.salary.viewed',
  offboardingStarted: 'hr.offboarding.started',
  offboardingCustomerHandoverRequested: 'hr.offboarding.customerHandoverRequested',
  offboardingHandoverRevertRequested: 'hr.offboarding.handoverRevertRequested',
  offboardingUnitReleaseRequested: 'hr.offboarding.unitReleaseRequested',
  offboardingAccountUnlinkRequested: 'hr.offboarding.accountUnlinkRequested',
  offboardingCompleted: 'hr.offboarding.completed',
  offboardingStuck: 'hr.offboarding.stuck',
  offboardingAborted: 'hr.offboarding.aborted',
  /** Bản nháp nghỉ việc do đồng bộ Elite Accounts đề xuất (không chạy saga) — notification mở / đóng mục vận hành. */
  offboardingDraftOpened: 'hr.offboardingDraft.opened',
  offboardingDraftClosed: 'hr.offboardingDraft.closed',
} as const;
export type EventType = (typeof EVENTS)[keyof typeof EVENTS];

export type EmployeeSource = 'manual' | 'import' | 'tenant_setup' | 'owner_recovery' | 'join_request';

export interface EmployeeRegistered {
  staffId: string;
  code: string;
  name: string;
  phone: string | null;
  email: string | null;
  title: string | null;
  contractType: ContractType;
  joinDate: string; // YYYY-MM-DD (Asia/Ho_Chi_Minh)
  teamId: string | null;
  defaultMonthlyTarget: string; // bigint VND dạng chuỗi
  /** Trạng thái hiện hành của hồ sơ: hr phát lại `registered` (cấp hồ sơ giao lại) cả khi người đó đã nghỉ ⇒ 'left'. */
  employmentStatus: 'active' | 'left';
  /**
   * ISO; chỉ khác null khi employmentStatus = 'left' (null cả với hồ sơ đã kích hoạt lại — khác DirectoryEntry.leftAt, vốn giữ lịch sử E9).
   * Bên tiêu thụ chỉ đọc khi employmentStatus = 'left'. hr luôn điền (tuỳ chọn trong kiểu vì fixture của bên tiêu thụ).
   */
  leftAt?: string | null;
  /** Vai trò ban đầu cho tài khoản (identity áp nếu hợp lệ). */
  initialRoleId: string | null;
  /** Liên kết đăng nhập ban đầu (yêu cầu tham gia / thiết lập / khôi phục chủ sở hữu). */
  link: { userId: string; joinRequestId: string | null } | null;
  source: EmployeeSource;
  /** Phản hồi cho identity.employeeProvisioning.requested (nếu có). */
  provisioningId?: string | null;
}

export interface EmployeeProfileUpdated {
  staffId: string;
  changes: Partial<{ name: string; code: string; phone: string | null; email: string | null; title: string | null; joinDate: string }>;
  previous: Partial<{ name: string; code: string; phone: string | null; email: string | null; title: string | null; joinDate: string }>;
}
export interface EmployeeContractTypeChanged {
  staffId: string;
  from: ContractType;
  to: ContractType;
}
export interface EmployeeTeamChanged {
  staffId: string;
  fromTeamId: string | null;
  toTeamId: string | null;
  reason: 'manual' | 'teamLeaderAssigned';
}
export interface EmployeeOffboarded {
  staffId: string;
  offboardingId: string;
  leftAt: string;
  handoverToStaffId: string;
  customerCount: number;
  taskCount: number;
  releasedUnitCount: number;
}
export interface EmployeeReactivated {
  staffId: string;
  reactivatedAt: string;
}
export interface TeamChanged {
  teamId: string;
  name: string;
  leaderId: string | null;
  managerId: string | null;
}
export interface TeamRosterChanged {
  teamId: string;
  deleted: boolean;
  leaderId: string | null;
  managerId: string | null;
  memberIds: string[];
  activeMemberIds: string[];
  affectedStaffIds: string[];
  rosterVersion: number;
}
export interface AccountUnlinkRequested {
  offboardingId: string;
  idempotencyKey: string;
  attempt: number;
  staffId: string;
}

// ---------- Payload sự kiện bổ sung (đặc tả hr §5) ----------
export type SalaryField = 'base' | 'allowance' | 'insuranceSalary';
export type OffboardingState = 'handover_pending' | 'units_pending' | 'account_pending' | 'compensating' | 'completed' | 'aborted';
export type OffboardingStep = 'handover' | 'units' | 'account' | 'handover_revert' | 'complete';

export interface EmployeeDefaultTargetChanged {
  staffId: string;
  from: string; // bigint VND dạng chuỗi
  to: string;
}
export interface TeamUpdated extends TeamChanged {
  previous: { name: string; leaderId: string | null; managerId: string | null };
}
export interface TeamDeleted {
  teamId: string;
  name: string;
}
/** Không bao giờ chứa số tiền. */
export interface SalaryChanged {
  staffId: string;
  revision: number;
  changedFields: SalaryField[];
}
/**
 * Bản ghi truy cập lương (hr §10.4, AT34) — không bao giờ chứa số tiền. Ghi qua outbox trong transaction riêng trước khi trả dữ liệu;
 * lệnh bị từ chối không ghi. aggregateId = staffId được xem (GetSalaryRecord); staffId người xem, hoặc tenantId nếu là hệ thống (GetSalarySnapshot).
 */
export interface SalaryViewed {
  viewerStaffId: string | null;
  query: 'GetSalaryRecord' | 'GetSalarySnapshot';
  /** Nhân sự được xem (GetSalaryRecord); null với ảnh chụp. */
  staffId: string | null;
  /** Số nhân sự có trong ảnh chụp trả về (GetSalarySnapshot). */
  staffCount?: number;
  /** Kỳ YYYY-MM của ảnh chụp (nếu bên gọi truyền). */
  period?: string | null;
}
export interface OffboardingStarted {
  offboardingId: string;
  staffId: string;
  staffName: string;
  successorId: string;
  successorName: string;
  effectiveAt: string;
  requestedBy: string | null;
}
export interface CustomerHandoverRequested {
  offboardingId: string;
  idempotencyKey: string; // '<offboardingId>:handover'
  attempt: number;
  fromStaffId: string;
  fromStaffName: string;
  toStaffId: string;
  toStaffName: string;
  actorStaffId: string | null;
  /** 'Bàn giao do {fromStaffName} nghỉ việc → {toStaffName}' */
  activityNote: string;
  scope: { customers: 'allOwned'; tasks: 'allOpen' };
}
export interface HandoverRevertRequested {
  offboardingId: string;
  idempotencyKey: string; // '<offboardingId>:handover-revert'
  attempt: number;
  fromStaffId: string;
  toStaffId: string;
  reason: string;
}
export interface UnitReleaseRequested {
  offboardingId: string;
  idempotencyKey: string; // '<offboardingId>:units'
  attempt: number;
  staffId: string;
  reason: 'offboarding';
}
export interface OffboardingCompleted {
  offboardingId: string;
  staffId: string;
  successorId: string;
  customerCount: number;
  taskCount: number;
  releasedUnitCount: number;
  completedAt: string;
}
export interface OffboardingStuck {
  offboardingId: string;
  staffId: string;
  step: OffboardingStep;
  attempt: number;
  lastError: string;
}
export interface OffboardingAborted {
  offboardingId: string;
  staffId: string;
  successorId: string;
  reason: string;
  revertedSteps: 'handover'[];
}

export type OffboardingDraftReason = 'departed' | 'suspended' | 'disabled' | 'grant_removed';
export type OffboardingDraftState = 'open' | 'started' | 'dismissed' | 'withdrawn';
/** aggregateId = draftId. */
export interface OffboardingDraftOpened {
  draftId: string;
  staffId: string;
  staffName: string;
  reason: OffboardingDraftReason;
}
/** aggregateId = draftId. started = đã bắt đầu nghỉ việc; dismissed = quản trị bỏ qua; withdrawn = Accounts có lại quyền. */
export interface OffboardingDraftClosed {
  draftId: string;
  staffId: string;
  state: Exclude<OffboardingDraftState, 'open'>;
}

/** Bản đồ loại sự kiện ⇒ payload (consumer: IntegrationEvent<hr.EventPayloads['hr.employee.registered']>). */
export interface EventPayloads {
  [EVENTS.employeeRegistered]: EmployeeRegistered;
  [EVENTS.employeeProfileUpdated]: EmployeeProfileUpdated;
  [EVENTS.employeeContractTypeChanged]: EmployeeContractTypeChanged;
  [EVENTS.employeeTeamChanged]: EmployeeTeamChanged;
  [EVENTS.employeeDefaultTargetChanged]: EmployeeDefaultTargetChanged;
  [EVENTS.employeeOffboarded]: EmployeeOffboarded;
  [EVENTS.employeeReactivated]: EmployeeReactivated;
  [EVENTS.teamCreated]: TeamChanged;
  [EVENTS.teamUpdated]: TeamUpdated;
  [EVENTS.teamDeleted]: TeamDeleted;
  [EVENTS.teamRosterChanged]: TeamRosterChanged;
  [EVENTS.salaryChanged]: SalaryChanged;
  [EVENTS.salaryViewed]: SalaryViewed;
  [EVENTS.offboardingStarted]: OffboardingStarted;
  [EVENTS.offboardingCustomerHandoverRequested]: CustomerHandoverRequested;
  [EVENTS.offboardingHandoverRevertRequested]: HandoverRevertRequested;
  [EVENTS.offboardingUnitReleaseRequested]: UnitReleaseRequested;
  [EVENTS.offboardingAccountUnlinkRequested]: AccountUnlinkRequested;
  [EVENTS.offboardingCompleted]: OffboardingCompleted;
  [EVENTS.offboardingStuck]: OffboardingStuck;
  [EVENTS.offboardingAborted]: OffboardingAborted;
  [EVENTS.offboardingDraftOpened]: OffboardingDraftOpened;
  [EVENTS.offboardingDraftClosed]: OffboardingDraftClosed;
}
type AssertNever<T extends never> = T;
/** Lỗi biên dịch nếu có sự kiện trong EVENTS chưa khai báo payload. */
export type _AllEventsHavePayload = AssertNever<Exclude<EventType, keyof EventPayloads>>;

/** Khoá idempotency của từng bước saga nghỉ việc — bên nhận lưu kết quả theo khoá này. */
export const offboardingKey = (offboardingId: string, step: 'handover' | 'handover-revert' | 'units' | 'account') => `${offboardingId}:${step}`;

// ---------- Hằng số dùng chung FE/BE ----------
export const CONTRACT_TYPES = ['official', 'probation', 'ctv'] as const satisfies readonly ContractType[];
export const CONTRACT_TYPE_LABEL: Record<ContractType, string> = { official: 'Chính thức', probation: 'Thử việc', ctv: 'Cộng tác viên' };
export const OFFBOARDING_STATES = ['handover_pending', 'units_pending', 'account_pending', 'compensating', 'completed', 'aborted'] as const satisfies readonly OffboardingState[];
export const OFFBOARDING_FINAL: readonly OffboardingState[] = ['completed', 'aborted'];

// ---------- Dữ liệu vào lệnh/truy vấn (zod — gateway truyền nguyên, service kiểm) ----------
const uuid = z.string().uuid();
const text = (max = 200) => z.string().trim().max(max);
const dateKey = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ngày dạng YYYY-MM-DD');
/** Tiền VND: số nguyên ≥ 0 (an toàn tới 9e15). */
const money = z.number({ invalid_type_error: 'Số tiền phải là số' }).int('Số tiền phải là số nguyên').min(0, 'Số tiền không được âm').max(9e15);
const emailField = z.preprocess((v) => (typeof v === 'string' ? v.trim().toLowerCase() : v), z.union([z.literal(''), z.string().email('Email không hợp lệ').max(200)]));
const contractType = z.enum(CONTRACT_TYPES);
/** ?roleIds=a,b hoặc mảng ⇒ mảng. */
const splitList = (v: unknown) => {
  if (v === undefined || v === null || v === '') return undefined;
  return (Array.isArray(v) ? v.flatMap((x) => String(x).split(',')) : String(v).split(',')).map((s) => s.trim()).filter(Boolean);
};
const listOf = <T extends z.ZodTypeAny>(item: T) => z.preprocess(splitList, z.array(item).max(50).optional());
const boolish = z.preprocess((v) => (v === 'true' || v === '1' || v === true ? true : v === 'false' || v === '0' || v === false ? false : undefined), z.boolean().optional());
const page = z.coerce.number().int().min(1).default(1);
const pageSize = (d = 100) => z.coerce.number().int().min(1).max(500).default(d);

export const staffRef = z.object({ staffId: uuid });
export const teamRef = z.object({ teamId: uuid });
export const offboardingRef = z.object({ offboardingId: uuid });

/** Tạo hồ sơ (form Nhân sự / "Duyệt & tạo hồ sơ"). code trống ⇒ cấp tự động; link ⇒ identity liên kết đăng nhập. */
export const registerEmployeeInput = z.object({
  name: text(120).min(1, 'Nhập họ tên'),
  code: text(32).min(1, 'Nhập mã nhân viên').optional(),
  phone: z.string().max(40).nullish(),
  email: emailField.nullish(),
  title: text(120).nullish(),
  contractType: contractType.default('official'),
  joinDate: dateKey.nullish(),
  teamId: uuid.nullish(),
  defaultMonthlyTarget: money.default(0),
  initialRoleId: z.string().trim().min(1).max(64).nullish(),
  link: z.object({ userId: uuid, joinRequestId: uuid.nullish() }).nullish(),
});
/** Sửa hồ sơ: trường vắng mặt = giữ nguyên, null = xoá giá trị. */
export const updateEmployeeInput = z.object({
  staffId: uuid,
  expectedVersion: z.number().int().min(0),
  name: text(120).min(1, 'Nhập họ tên').optional(),
  code: text(32).min(1, 'Nhập mã nhân viên').optional(),
  phone: z.string().max(40).nullable().optional(),
  email: emailField.nullable().optional(),
  title: text(120).nullable().optional(),
  /** Đổi được, không xoá được (hr.employee.registered luôn mang ngày vào làm). */
  joinDate: dateKey.optional(),
  contractType: contractType.optional(),
  teamId: uuid.nullable().optional(),
  defaultMonthlyTarget: money.optional(),
});
export const reactivateEmployeeInput = z.object({ staffId: uuid, expectedVersion: z.number().int().min(0) });
export const setSalaryInput = z.object({
  staffId: uuid,
  base: money,
  allowance: money,
  insuranceSalary: money,
  expectedRevision: z.number().int().min(0).optional(),
});

export const createTeamInput = z.object({ name: text(120).min(1, 'Nhập tên nhóm'), leaderId: uuid.nullish(), managerId: uuid.nullish() });
/** Sửa nhóm: leaderId/managerId vắng mặt = giữ nguyên, null = bỏ trống. */
export const updateTeamInput = z.object({
  teamId: uuid,
  expectedVersion: z.number().int().min(0),
  name: text(120).min(1, 'Nhập tên nhóm'),
  leaderId: uuid.nullable().optional(),
  managerId: uuid.nullable().optional(),
});

/** draftId: bắt đầu từ bản nháp do đồng bộ Accounts đề xuất ⇒ bản nháp chuyển `started`. */
export const startOffboardingInput = z.object({ staffId: uuid, successorId: uuid, draftId: uuid.optional() });
export const listOffboardingDraftsInput = z.object({ state: z.enum(['open', 'all']).default('open'), staffId: uuid.optional() });
export const dismissOffboardingDraftInput = z.object({ draftId: uuid, note: text(500).min(1, 'Nhập lý do bỏ qua') });
/** Hồ sơ đã liên kết Elite Accounts: họ tên + email do Accounts quản, sửa ở Cathouse bị từ chối với mã này. */
export const ACCOUNTS_MANAGED_FIELD = 'ACCOUNTS_MANAGED_FIELD';
export const compensateOffboardingInput = z.object({ offboardingId: uuid, reason: text(500).min(1, 'Nhập lý do') });

export const listEmployeesInput = z.object({
  /** active = chưa nghỉ (gồm tài khoản bị khoá). */
  status: z.enum(['active', 'left', 'all']).default('active'),
  q: z.string().trim().max(200).optional(),
  roleIds: listOf(z.string().min(1).max(64)),
  teamId: uuid.optional(),
  contractType: contractType.optional(),
  sort: z.enum(['name', '-name', 'code', '-code', 'joinDate', '-joinDate']).default('name'),
  page,
  pageSize: pageSize(100),
});
export const listSuccessorCandidatesInput = z.object({ excludeStaffId: uuid.optional() });
export const listOffboardingsInput = z.object({ state: z.enum(OFFBOARDING_STATES).optional(), stuck: boolish, page, pageSize: pageSize(50) });

// Truy vấn nội bộ service ↔ service (không mở qua gateway)
export const listTeamRostersInput = z.object({ afterTeamId: uuid.optional(), limit: z.coerce.number().int().min(1).max(1000).default(500) });
export const directorySnapshotInput = z.object({ afterStaffId: uuid.optional(), limit: z.coerce.number().int().min(1).max(1000).default(500), updatedSince: z.string().datetime().optional() });
export const salarySnapshotInput = z.object({ staffIds: z.array(uuid).max(1000), period: z.string().regex(/^\d{4}-\d{2}$/).optional() });
/** Số nhân sự chưa nghỉ (checklist "Thêm nhân sự" ở Tổng quan) — mọi thành viên đã đăng nhập, không cần hr.view. */
export const getHeadcountInput = z.object({});

/** Tên RPC: hr.cmd.<Name> / hr.qry.<Name>. */
export const RPC = {
  cmd: [
    'RegisterEmployee',
    'UpdateEmployee',
    'ReactivateEmployee',
    'SetSalaryRecord',
    'CreateTeam',
    'UpdateTeam',
    'DeleteTeam',
    'StartOffboarding',
    'RetryOffboarding',
    'CompensateOffboarding',
    'DismissOffboardingDraft',
  ],
  qry: [
    'ListEmployees',
    'GetEmployee',
    'GetNextEmployeeCode',
    'ListSuccessorCandidates',
    'ListTeams',
    'GetTeam',
    'GetSalaryRecord',
    'GetOffboarding',
    'GetActiveOffboardingByStaff',
    'ListOffboardings',
    'ListOffboardingDrafts',
    'GetHeadcount',
    // nội bộ
    'ListTeamRosters',
    'GetEmployeeDirectorySnapshot',
    'GetSalarySnapshot',
  ],
} as const;

// ---------- Kết quả trả về (read model) ----------
/** Trạng thái hiển thị: left nếu đã nghỉ; locked nếu tài khoản bị khoá; còn lại active. */
export type EmployeeStatus = 'active' | 'locked' | 'left';
/** Bản sao trạng thái tài khoản từ identity (chỉ đọc). */
export type AccountMirrorStatus = 'unlinked' | 'active' | 'locked' | 'left';

export interface EmployeeView {
  staffId: string;
  code: string;
  name: string;
  title: string | null;
  phone: string | null;
  email: string | null;
  contractType: ContractType;
  joinDate: string | null;
  teamId: string | null;
  teamName: string | null;
  roleId: string | null;
  userId: string | null;
  accountStatus: AccountMirrorStatus;
  status: EmployeeStatus;
  employmentStatus: 'active' | 'left';
  isSeller: boolean;
  defaultMonthlyTarget: number;
  offboardingInProgress: boolean;
  activeOffboardingId: string | null;
  leftAt: string | null;
  handoverToStaffId: string | null;
  handoverToName: string | null;
  /** Hồ sơ đã đồng bộ từ Elite Accounts ⇒ họ tên + email chỉ đọc (sửa ở Accounts). */
  accountsManaged: boolean;
  version: number;
}
export interface EmployeeList {
  rows: EmployeeView[];
  total: number;
  /** Bộ đếm tab trong phạm vi người xem. */
  counts: { active: number; left: number };
  page: number;
  pageSize: number;
}
/** Thông tin Elite Accounts của hồ sơ (chỉ consumer đồng bộ ghi). */
export interface EmployeeAccountsInfo {
  employeeCode: string | null;
  jobTitle: string | null;
  departmentName: string | null;
  managerStaffId: string | null;
  status: 'active' | 'on_leave' | 'suspended' | 'departed' | null;
  syncedAt: string;
}
export interface OffboardingDraftView {
  draftId: string;
  staffId: string;
  staffName: string;
  userId: string | null;
  source: 'accounts';
  reason: OffboardingDraftReason;
  accountsStatus: string | null;
  detectedAt: string;
  state: OffboardingDraftState;
  offboardingId: string | null;
  closedAt: string | null;
  closedBy: string | null;
  note: string | null;
}
export interface EmployeeDetail extends EmployeeView {
  accounts: EmployeeAccountsInfo | null;
  /** Bản nháp nghỉ việc đang mở (chỉ trả cho người có hr.manage). */
  offboardingDraft: OffboardingDraftView | null;
  source: EmployeeSource;
  reactivatedAt: string | null;
  createdAt: string;
  updatedAt: string;
  self: boolean;
}
export interface RegisterEmployeeResult {
  staffId: string;
  code: string;
  version: number;
}
export interface TeamCard {
  teamId: string;
  name: string;
  leaderId: string | null;
  leaderName: string | null;
  managerId: string | null;
  managerName: string | null;
  activeMemberCount: number;
  activeMembers: { staffId: string; name: string; code: string }[];
  rosterVersion: number;
  version: number;
}
export interface SalaryRecordView {
  staffId: string;
  /** false với CTV (không áp dụng lương cơ bản). */
  applicable: boolean;
  /** false = chưa có bản ghi (các khoản = 0). */
  exists: boolean;
  base: number;
  allowance: number;
  insuranceSalary: number;
  revision: number;
  updatedAt: string | null;
  updatedBy: string | null;
}
export interface SuccessorCandidate {
  staffId: string;
  name: string;
  code: string;
  title: string | null;
  teamId: string | null;
}
export interface OffboardingStepView {
  step: OffboardingStep;
  attempt: number;
  outcome: 'requested' | 'ok' | 'rejected' | 'timeout' | 'skipped' | 'stuck';
  at: string;
  detail: Record<string, unknown> | null;
}
export interface OffboardingView {
  offboardingId: string;
  staffId: string;
  staffName: string;
  successorId: string;
  successorName: string;
  effectiveAt: string;
  requestedBy: string | null;
  state: OffboardingState;
  step: OffboardingStep;
  attempt: number;
  stuckAt: string | null;
  lastError: string | null;
  customerCount: number | null;
  taskCount: number | null;
  releasedUnitCount: number | null;
  startedAt: string;
  completedAt: string | null;
  abortedAt: string | null;
  abortReason: string | null;
  version: number;
  timeline?: OffboardingStepView[];
}
export interface OffboardingList {
  rows: OffboardingView[];
  total: number;
  page: number;
  pageSize: number;
}
/** Truy vấn nội bộ ListTeamRosters (identity dựng lại phạm vi nhóm). */
export type TeamRoster = Omit<TeamRosterChanged, 'affectedStaffIds'>;
/** Truy vấn nội bộ GetEmployeeDirectorySnapshot — chỉ trường không nhạy cảm. */
export interface DirectoryEntry {
  staffId: string;
  code: string;
  name: string;
  phone: string | null;
  email: string | null;
  title: string | null;
  contractType: ContractType;
  joinDate: string | null;
  teamId: string | null;
  defaultMonthlyTarget: string;
  employmentStatus: 'active' | 'left';
  /** Lần nghỉ gần nhất (ISO), giữ cả sau khi kích hoạt lại (E9) — chỉ có nghĩa khi employmentStatus = 'left'. */
  leftAt: string | null;
  updatedAt: string;
  /**
   * Phiên bản aggregate Employee (= aggregateVersion của sự kiện hr.employee.* mới nhất của người này) — bên dựng projection
   * so với phiên bản theo nhóm trường để sự kiện cũ (giao lại từ DLQ) không ghi đè. hr luôn điền; tuỳ chọn vì fixture của bên tiêu thụ.
   */
  version?: number;
}
/** GetHeadcount: số nhân sự chưa nghỉ (gồm tài khoản bị khoá). */
export interface Headcount {
  active: number;
}
/** Truy vấn nội bộ GetSalarySnapshot (commission chốt kỳ chi trả). */
export interface SalarySnapshotItem {
  staffId: string;
  contractType: ContractType;
  base: number;
  allowance: number;
  insuranceSalary: number;
  revision: number;
}
